import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Alert,
	Box,
	CircularProgress,
	Collapse,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from 'components/mui';
import { ExpandLess, ExpandMore } from 'components/icons';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import { useIsMobile } from 'hooks/useIsMobile';
import { GetListRoster } from 'api/canmail/CANmailAPI';
import {
	getLotusBlossomForwardedHost,
	LotusBlossomPurchase,
	PurchasesByEmail,
} from 'api/lotusBlossom/lotusBlossomAPI';
import { RosterContact } from '../libs/participants';
import { buildClients, Client, filterClients } from '../libs/clients';
import PurchaseHistory from './PurchaseHistory';

interface ClientsPanelProps {
	participantsListId: number | null;
	/** From the page's single Stripe read; null while it is still loading. */
	purchases: PurchasesByEmail | null;
	/** Hands a cancelled or resumed membership back to that read. */
	onMembershipChange: (updated: LotusBlossomPurchase) => void;
}

const NO_PURCHASES: PurchasesByEmail = {};

type LoadState = 'loading' | 'error' | 'ready';

const EMPTY_CELL = '—';

/**
 * The participants list joined to Stripe on email, which is the only field the
 * two systems share.
 */
function ClientsPanel({ participantsListId, purchases, onMembershipChange }: ClientsPanelProps) {
	const { notifyError } = useNotificationProvider();
	const isMobile = useIsMobile();
	const [state, setState] = useState<LoadState>('loading');
	const [contacts, setContacts] = useState<RosterContact[]>([]);
	const [expanded, setExpanded] = useState<string | null>(null);
	const [search, setSearch] = useState('');
	// Bumped per load and on unmount, so a response that has been overtaken —
	// or arrives after the admin has left the tab — is dropped, error toast
	// included.
	const requestRef = useRef(0);

	const load = useCallback(async () => {
		const request = ++requestRef.current;
		const isCurrent = () => request === requestRef.current;

		setState('loading');
		try {
			if (!participantsListId) {
				setContacts([]);
				setState('ready');
				return;
			}

			// The directory shows as soon as the list arrives; purchase counts
			// fill in when the page's Stripe read does.
			const roster = await GetListRoster(participantsListId, getLotusBlossomForwardedHost());
			if (!isCurrent()) return;

			setContacts(roster?.contacts ?? []);
			setState('ready');
		} catch (error) {
			if (!isCurrent()) return;
			console.error('[QuantumHealing] ClientsPanel load:', error);
			notifyError('Could not load the client list.');
			setState('error');
		}
	}, [participantsListId, notifyError]);

	useEffect(() => {
		load();
		return () => {
			requestRef.current += 1;
		};
	}, [load]);

	const purchasesLoading = purchases === null;
	const clients = useMemo(
		() => buildClients(contacts, purchases ?? NO_PURCHASES),
		[contacts, purchases]
	);
	const visible = useMemo(() => filterClients(clients, search), [clients, search]);

	const toggle = (id: string) =>
		setExpanded((current) => (current === id ? null : id));

	const emptyMessage =
		clients.length === 0
			? 'Nobody has been added to the participants list yet.'
			: 'No clients match your search.';

	if (state === 'error') {
		return (
			<Alert severity="error">
				We couldn&apos;t load the client list. Please refresh the page — if it
				keeps happening, contact support.
			</Alert>
		);
	}

	// Without this the panel would render its empty state — "0 clients", "Nobody
	// has been added…" — until the list arrives.
	if (state === 'loading') {
		return (
			<Paper elevation={1} sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
				<CircularProgress aria-label="Loading clients" />
			</Paper>
		);
	}

	if (state === 'ready' && !participantsListId) {
		return (
			<Paper elevation={1} sx={{ p: { xs: 2, sm: 3 } }}>
				<Typography variant="h6" sx={{ fontWeight: 600 }}>
					No client list found
				</Typography>
				<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
					This tab reads the &quot;Quantum Healing Participants&quot; mailing
					list, which doesn&apos;t exist yet. Create it in List Manager to see
					clients here.
				</Typography>
			</Paper>
		);
	}

	return (
		<Paper elevation={1}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					gap: 2,
					alignItems: { sm: 'center' },
					justifyContent: 'space-between',
					p: { xs: 2, sm: 2.5 },
				}}
			>
				<TextField
					label="Search clients"
					size="small"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					sx={{ flex: 1, minWidth: 0, maxWidth: { sm: 420 } }}
				/>

				<Typography variant="body2" color="text.secondary">
					{visible.length === clients.length
						? `${clients.length} client${clients.length === 1 ? '' : 's'}`
						: `${visible.length} of ${clients.length} clients`}
				</Typography>
			</Box>

			{isMobile ? (
				<Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
					{visible.length === 0 && (
						<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
							{emptyMessage}
						</Typography>
					)}

					{visible.map((client) => (
						<ClientCard
							key={client.id}
							client={client}
							open={expanded === client.id}
							onToggle={() => toggle(client.id)}
							purchasesLoading={purchasesLoading}
							onMembershipChange={onMembershipChange}
						/>
					))}
				</Box>
			) : (
				// No TableContainer: it sets overflow-x, and a component may not
				// define its own scrolling.
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell sx={{ width: 48 }} />
							<TableCell>Name</TableCell>
							<TableCell>Email</TableCell>
							<TableCell>Phone</TableCell>
							<TableCell align="right">Purchases</TableCell>
						</TableRow>
					</TableHead>

					<TableBody>
						{visible.length === 0 && (
							<TableRow>
								<TableCell colSpan={5}>
									<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
										{emptyMessage}
									</Typography>
								</TableCell>
							</TableRow>
						)}

						{visible.map((client) => (
							<ClientRow
								key={client.id}
								client={client}
								open={expanded === client.id}
								onToggle={() => toggle(client.id)}
								purchasesLoading={purchasesLoading}
								onMembershipChange={onMembershipChange}
							/>
						))}
					</TableBody>
				</Table>
			)}
		</Paper>
	);
}

interface ClientRowProps {
	client: Client;
	open: boolean;
	onToggle: () => void;
	/** Stripe hasn't answered yet, so "no purchases" would be a guess. */
	purchasesLoading: boolean;
	onMembershipChange: (updated: LotusBlossomPurchase) => void;
}

function PurchaseDetail({
	client,
	purchasesLoading,
	onMembershipChange,
}: Pick<ClientRowProps, 'client' | 'purchasesLoading' | 'onMembershipChange'>) {
	return purchasesLoading ? (
		<Typography variant="body2" color="text.secondary">
			Loading purchase history…
		</Typography>
	) : (
		<PurchaseHistory purchases={client.purchases} onMembershipChange={onMembershipChange} />
	);
}

function ClientCard({
	client,
	open,
	onToggle,
	purchasesLoading,
	onMembershipChange,
}: ClientRowProps) {
	return (
		<Paper
			elevation={0}
			onClick={onToggle}
			sx={{
				p: 2,
				border: 1,
				borderColor: 'divider',
				borderRadius: 2,
				cursor: 'pointer',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'flex-start',
					gap: 1,
				}}
			>
				<Box sx={{ minWidth: 0 }}>
					<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
						{client.name}
					</Typography>
					<Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
						{client.email || EMPTY_CELL}
					</Typography>
					<Typography
						variant="body2"
						color={client.phone ? 'text.secondary' : 'text.disabled'}
					>
						{client.phone || EMPTY_CELL}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{purchasesLoading
							? 'Loading purchases…'
							: `${client.purchases.length} purchase${
									client.purchases.length === 1 ? '' : 's'
							  }`}
					</Typography>
				</Box>

				<IconButton
					size="small"
					aria-label={open ? 'Hide purchase history' : 'Show purchase history'}
				>
					{open ? <ExpandLess /> : <ExpandMore />}
				</IconButton>
			</Box>

			<Collapse in={open} timeout="auto" unmountOnExit>
				<Box sx={{ pt: 2 }}>
					<PurchaseDetail
						client={client}
						purchasesLoading={purchasesLoading}
						onMembershipChange={onMembershipChange}
					/>
				</Box>
			</Collapse>
		</Paper>
	);
}

// Detail sits in a second full-width row: DataGrid's master-detail is Pro-only,
// so this is a plain Table.
function ClientRow({
	client,
	open,
	onToggle,
	purchasesLoading,
	onMembershipChange,
}: ClientRowProps) {
	return (
		<>
			<TableRow
				hover
				onClick={onToggle}
				sx={{ cursor: 'pointer', '& > *': { borderBottom: open ? 'unset' : undefined } }}
			>
				<TableCell sx={{ width: 48 }}>
					<IconButton
						size="small"
						aria-label={open ? 'Hide purchase history' : 'Show purchase history'}
					>
						{open ? <ExpandLess /> : <ExpandMore />}
					</IconButton>
				</TableCell>

				<TableCell>{client.name}</TableCell>

				<TableCell sx={{ wordBreak: 'break-word' }}>
					{client.email || EMPTY_CELL}
				</TableCell>

				<TableCell>
					<Typography
						variant="body2"
						color={client.phone ? 'text.primary' : 'text.disabled'}
					>
						{client.phone || EMPTY_CELL}
					</Typography>
				</TableCell>

				<TableCell align="right">
					<Typography
						variant="body2"
						color={
							!purchasesLoading && client.purchases.length
								? 'text.primary'
								: 'text.disabled'
						}
					>
						{purchasesLoading ? '…' : client.purchases.length || EMPTY_CELL}
					</Typography>
				</TableCell>
			</TableRow>

			<TableRow>
				<TableCell sx={{ py: 0, borderBottom: open ? undefined : 'none' }} colSpan={5}>
					<Collapse in={open} timeout="auto" unmountOnExit>
						<Box sx={{ py: 2 }}>
							<PurchaseDetail
								client={client}
								purchasesLoading={purchasesLoading}
								onMembershipChange={onMembershipChange}
							/>
						</Box>
					</Collapse>
				</TableCell>
			</TableRow>
		</>
	);
}

export default ClientsPanel;
